import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { makeBranchApi } from "../../api/branchApi";
import { PageHeader, LoadingSpinner, StatCard } from "../../components/ui";
import toast from "react-hot-toast";

const TABS = ["Daily", "Staff Performance", "Stations"];

function today() {
  return new Date().toISOString().split("T")[0];
}

export default function BranchReportsPage() {
  const { branchId } = useParams();
  const api = makeBranchApi(branchId);

  const [tab,     setTab]    = useState(0);
  const [date,    setDate]   = useState(today());
  const [loading, setLoading]= useState(false);
  const [data,    setData]   = useState(null);

  const load = async () => {
    setLoading(true);
    setData(null);
    try {
      let res;
      if (tab === 0) res = await api.dailyReport({ date });
      if (tab === 1) res = await api.staffReport({ date_from: date, date_to: date });
      if (tab === 2) res = await api.stationReport({ date_from: date, date_to: date });
      setData(res.data);
    } catch (err) {
      toast.error("Failed to load report.");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [tab, date, branchId]);

  return (
    <div style={styles.root}>
      <PageHeader title="Reports" subtitle="Queue performance analytics for this branch." />

      <div style={styles.toolbar}>
        <div style={styles.tabs}>
          {TABS.map((t, i) => (
            <button key={t} onClick={() => setTab(i)}
              style={{ ...styles.tab, ...(tab === i ? styles.tabActive : {}) }}>
              {t}
            </button>
          ))}
        </div>
        <input type="date" style={styles.datePicker} value={date} onChange={e => setDate(e.target.value)} />
      </div>

      {loading ? <LoadingSpinner /> : !data ? null : (
        <>
          {tab === 0 && <DailyView data={data} />}
          {tab === 1 && <StaffView data={data} />}
          {tab === 2 && <StationView data={data} />}
        </>
      )}
    </div>
  );
}

function DailyView({ data }) {
  return (
    <div>
      <div style={styles.statsRow}>
        <StatCard label="Total Tickets"   value={data.total_tickets}   color="#60A5FA" />
        <StatCard label="Completed"        value={data.completed}        color="#22C55E" />
        <StatCard label="Still in Queue"   value={data.still_in_queue}   color="#EAB308" />
        <StatCard label="Abandoned"        value={data.abandoned}        color="#EF4444" />
        <StatCard label="Completion Rate"  value={data.completion_rate != null ? `${data.completion_rate}%` : null} color="#A78BFA" />
        <StatCard label="Avg Wait (min)"   value={data.avg_wait_minutes} color="#F472B6" />
      </div>

      {data.hourly_volume && (
        <div style={styles.card}>
          <div style={styles.cardTitle}>Hourly Volume</div>
          <div style={styles.hourlyRow}>
            {Array.from({ length: 24 }, (_, h) => {
              const count = data.hourly_volume[h] || 0;
              const max   = Math.max(...Object.values(data.hourly_volume || {}), 1);
              const pct   = Math.round((count / max) * 100);
              return (
                <div key={h} style={styles.hourBar}>
                  <div style={{ ...styles.bar, height: `${Math.max(pct, 4)}%`, opacity: count ? 1 : 0.2 }} />
                  <div style={styles.hourLabel}>{h}h</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {data.station_breakdown?.length > 0 && (
        <div style={styles.card}>
          <div style={styles.cardTitle}>Station Breakdown</div>
          <table style={styles.table}>
            <thead><tr>
              {["Station", "Served", "Avg Wait (min)", "Avg Service (min)"].map(h =>
                <th key={h} style={styles.th}>{h}</th>
              )}
            </tr></thead>
            <tbody>
              {data.station_breakdown.map(s => (
                <tr key={s.station_name} style={styles.tr}>
                  <td style={styles.td}>
                    <div style={{ color: "#E2E8F0", fontWeight: "500" }}>{s.station_name}</div>
                    {s.zone_name && <div style={{ fontSize: "11px", color: "#475569" }}>{s.zone_name}</div>}
                  </td>
                  <td style={{ ...styles.td, color: "#60A5FA", fontWeight: "700" }}>{s.served}</td>
                  <td style={{ ...styles.td, color: "#EAB308" }}>{s.avg_wait_minutes ?? 0}</td>
                  <td style={{ ...styles.td, color: "#22C55E" }}>{s.avg_service_minutes ?? 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function StaffView({ data }) {
  const staff = data?.staff || data?.results || [];
  if (!staff.length) return <div style={styles.empty}>No staff activity for this period.</div>;
  return (
    <div style={styles.card}>
      <div style={styles.cardTitle}>Staff Performance</div>
      <table style={styles.table}>
        <thead><tr>
          {["Staff", "Station", "Served", "Avg Service (min)"].map(h =>
            <th key={h} style={styles.th}>{h}</th>
          )}
        </tr></thead>
        <tbody>
          {staff.map((s, i) => (
            <tr key={i} style={styles.tr}>
              <td style={styles.td}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <div style={styles.avatar}>{s.staff_name?.[0]?.toUpperCase() || "?"}</div>
                  <span style={{ color: "#E2E8F0", fontWeight: "500" }}>{s.staff_name}</span>
                </div>
              </td>
              <td style={{ ...styles.td, color: "#94A3B8" }}>{s.station_name || "—"}</td>
              <td style={{ ...styles.td, color: "#60A5FA", fontWeight: "700" }}>{s.total_served}</td>
              <td style={{ ...styles.td, color: "#22C55E" }}>{s.avg_service_minutes ?? 0}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function StationView({ data }) {
  const [expanded, setExpanded] = React.useState({});
  const toggle = (name) => setExpanded(p => ({ ...p, [name]: !p[name] }));
  const stations = data?.stations || data?.results || [];
  if (!stations.length) return <div style={styles.empty}>No station activity for this period.</div>;
  return (
    <div style={styles.card}>
      <div style={styles.cardTitle}>Station Performance</div>
      <table style={styles.table}>
        <thead><tr>
          {["Station", "Visits", "Served", "Expired", "Avg Wait", "Avg Service", "Bottleneck?"].map(h =>
            <th key={h} style={styles.th}>{h}</th>
          )}
        </tr></thead>
        <tbody>
          {stations.map((s, i) => (
            <React.Fragment key={i}>
              <tr style={{ ...styles.tr, cursor: s.counters?.length > 0 ? "pointer" : "default" }}
                onClick={() => s.counters?.length > 0 && toggle(s.station_name)}>
                <td style={styles.td}>
                  <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    {s.counters?.length > 0 && <span style={{ fontSize: "10px", color: "#475569" }}>{expanded[s.station_name] ? "▾" : "▸"}</span>}
                    <div>
                      <div style={{ color: "#E2E8F0", fontWeight: "500" }}>{s.station_name}</div>
                      <div style={{ fontSize: "11px", color: "#475569" }}>{s.zone_name}</div>
                    </div>
                  </div>
                </td>
                <td style={{ ...styles.td, textAlign: "center" }}>{s.total_visits ?? 0}</td>
                <td style={{ ...styles.td, textAlign: "center", color: "#22C55E" }}>{s.served ?? 0}</td>
                <td style={{ ...styles.td, textAlign: "center", color: s.expired_at_station > 0 ? "#F97316" : "#334155" }}>{s.expired_at_station ?? 0}</td>
                <td style={{ ...styles.td, textAlign: "center", color: s.avg_wait_minutes > 15 ? "#EF4444" : "#EAB308" }}>{s.avg_wait_minutes ?? 0} min</td>
                <td style={{ ...styles.td, textAlign: "center", color: "#22C55E" }}>{s.avg_service_minutes ?? 0} min</td>
                <td style={{ ...styles.td, textAlign: "center" }}>
                  {s.is_bottleneck ? (
                    <div>
                      <span style={{ color: "#EF4444", fontSize: "12px", fontWeight: "600" }}>⚠ Yes</span>
                      {s.bottleneck_reason && <div style={{ fontSize: "10px", color: "#EF4444" }}>{s.bottleneck_reason}</div>}
                    </div>
                  ) : <span style={{ color: "#334155" }}>—</span>}
                </td>
              </tr>
              {expanded[s.station_name] && s.counters?.map((c, ci) => (
                <tr key={`${i}-${ci}`} style={{ ...styles.tr, background: "rgba(37,99,235,0.04)" }}>
                  <td style={{ ...styles.td, paddingLeft: "36px" }}>
                    <span style={{ fontSize: "12px", color: "#60A5FA" }}>Counter {c.counter_number ?? ci+1}: {c.counter_name}</span>
                  </td>
                  <td style={{ ...styles.td, textAlign: "center", fontSize: "12px" }}>{c.total_visits ?? 0}</td>
                  <td style={{ ...styles.td, textAlign: "center", fontSize: "12px", color: "#22C55E" }}>{c.served ?? 0}</td>
                  <td style={{ ...styles.td, textAlign: "center", fontSize: "12px" }}>—</td>
                  <td style={{ ...styles.td, textAlign: "center", fontSize: "12px", color: c.avg_wait_minutes > 15 ? "#EF4444" : "#64748B" }}>{c.avg_wait_minutes ?? 0} min</td>
                  <td style={{ ...styles.td, textAlign: "center", fontSize: "12px" }}>{c.avg_service_minutes ?? 0} min</td>
                  <td style={{ ...styles.td, textAlign: "center" }}>
                    {c.avg_wait_minutes > 15 ? <span style={{ color: "#EF4444", fontSize: "11px" }}>⚠ Slow</span> : <span style={{ color: "#334155" }}>—</span>}
                  </td>
                </tr>
              ))}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const styles = {
  root:        { padding: "32px", fontFamily: "'DM Sans','Segoe UI',sans-serif", maxWidth: "1000px" },
  toolbar:     { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "24px" },
  tabs:        { display: "flex", gap: "4px", borderBottom: "1px solid rgba(37,99,235,0.15)" },
  tab:         { background: "none", border: "none", borderBottom: "2px solid transparent", padding: "10px 16px", fontSize: "13px", fontWeight: "500", color: "#64748B", cursor: "pointer", fontFamily: "inherit", marginBottom: "-1px" },
  tabActive:   { color: "#60A5FA", borderBottomColor: "#2563EB" },
  datePicker:  { background: "rgba(30,41,59,0.8)", border: "1px solid rgba(51,65,85,0.8)", borderRadius: "8px", padding: "8px 12px", color: "#F1F5F9", fontSize: "13px", outline: "none", fontFamily: "inherit" },
  statsRow:    { display: "flex", gap: "14px", flexWrap: "wrap", marginBottom: "24px" },
  card:        { background: "rgba(15,23,42,0.6)", border: "1px solid rgba(37,99,235,0.12)", borderRadius: "14px", padding: "20px", marginBottom: "16px" },
  cardTitle:   { fontSize: "12px", fontWeight: "700", color: "#475569", letterSpacing: "0.07em", textTransform: "uppercase", marginBottom: "16px" },
  hourlyRow:   { display: "flex", gap: "3px", alignItems: "flex-end", height: "80px" },
  hourBar:     { flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: "4px", height: "100%" },
  bar:         { width: "100%", background: "#2563EB", borderRadius: "3px 3px 0 0", minHeight: "4px" },
  hourLabel:   { fontSize: "9px", color: "#334155" },
  table:       { width: "100%", borderCollapse: "collapse" },
  th:          { padding: "10px 12px", fontSize: "11px", fontWeight: "700", color: "#475569", textAlign: "left", letterSpacing: "0.06em", textTransform: "uppercase", borderBottom: "1px solid rgba(37,99,235,0.1)" },
  tr:          { borderBottom: "1px solid rgba(37,99,235,0.06)" },
  td:          { padding: "11px 12px", fontSize: "13px", verticalAlign: "middle" },
  avatar:      { width: "28px", height: "28px", borderRadius: "6px", background: "linear-gradient(135deg,#2563EB,#7C3AED)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "12px", fontWeight: "700", color: "#fff", flexShrink: 0 },
  empty:       { color: "#475569", fontSize: "14px", padding: "32px", textAlign: "center" },
};
